import React, { useState } from "react";
import { FiLoader, FiUser, FiUserMinus } from "react-icons/fi";
import { useQueryClient } from "@tanstack/react-query";
import RemoveMembers from "./RemoveMembers";
import GroupInvite from "./GroupInvite";
import { useCourse } from "../context/CourseContext";
import { useAuth } from "../context/AuthContext";
import { apiGet, useAccountQuery } from "../utils/queries";
import { useGroupSearch } from "../context/GroupSearchContext";

const Learners = () => {
  const { course } = useCourse();
  const [query] = useGroupSearch();
  const { userId } = useAuth();
  const client = useQueryClient();
  const [removing, setRemoving] = useState(null);
  const result = useAccountQuery(["members", course._id], null, {
    queryFn: async ({ signal }) => {
      return { members: await apiGet(`/workspace/manageMembers/${course._id}`, signal) };
    },
    enabled: Boolean(userId && course?._id),
  });
  const members = result.data?.members;
  const owner = String(course.creatorId?._id || course.creatorId) === String(userId)
    || members?.some(member => String(member.userId?._id || member.userId) === String(userId) && member.role === "owner");
  const updateMembers = async update => {
    await client.cancelQueries({ queryKey: result.queryKey, exact: true });
    client.setQueryData(result.queryKey, previous => previous ? { ...previous, members: update(previous.members) } : previous);
    client.invalidateQueries({ queryKey: ["account", userId, "community-preview", course._id] });
    client.invalidateQueries({ queryKey: ["account", userId, "workspaces"] });
  };
  const error = result.error?.message || "";
  const filtered = members?.filter(member => `${member.userId?.username || "Member"} ${member.role}`.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <main className="w-full max-w-5xl px-4 py-6 sm:px-6">
      {owner && <div className="mb-6 flex justify-end"><GroupInvite slug={course.slug} compact /></div>}
      {error && !members ? <div><p role="alert" className="text-sm text-red-700">{error}</p><button onClick={() => result.refetch()} className="mt-3 rounded-md border border-gray-300 px-3 py-2 text-sm">Try again</button></div>
        : !members ? <div role="status" aria-label="Loading members" className="flex min-h-48 items-center justify-center"><FiLoader className="h-6 w-6 animate-spin text-orange-700" /></div>
          : <div className="overflow-x-auto rounded-lg border border-[var(--app-border)] bg-[var(--app-panel,white)]"><table aria-label="Members" className="w-full text-left text-sm">
            <thead className="border-b border-[var(--app-border)] bg-[var(--app-muted)] text-[var(--app-secondary)]"><tr><th scope="col" className="px-2 py-3 sm:px-4 font-semibold">Member</th><th scope="col" className="px-2 py-3 sm:px-4 font-semibold">Role</th><th scope="col" className="px-2 py-3 sm:px-4 text-right font-semibold">Actions</th></tr></thead><tbody className="divide-y divide-[var(--app-border)]">
            {filtered.map((member, index) => {
              return <tr key={member.userId?._id || index} className="hover:bg-[var(--app-muted)]">
              <td className="px-2 py-3 sm:px-4">
              <div className="flex min-w-0 items-center gap-3">
              <span className="hidden h-10 w-10 shrink-0 sm:flex items-center justify-center rounded-full bg-blue-50 text-blue-700"><FiUser aria-hidden="true" /></span>
              <span className="break-words text-sm font-medium">{member.userId?.username || "Member"}</span>
              </div>
              </td><td className="px-2 py-3 sm:px-4">
              <span className="rounded border border-[var(--app-border)] bg-[var(--app-surface)] px-2 py-1 text-xs font-semibold capitalize">{member.role}</span>
              </td><td className="px-2 py-3 sm:px-4"><div className="flex flex-wrap items-center justify-end gap-2">
              {owner && member.role !== "owner" && String(member.userId?._id) !== String(course.creatorId?._id || course.creatorId) && String(member.userId?._id) !== String(userId) && <button type="button" title="Remove member" aria-label={`Remove ${member.userId?.username || "member"}`} onClick={() => setRemoving({ id: member.userId._id, username: member.userId.username || "Member" })} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-red-700 hover:bg-red-50"><FiUserMinus aria-hidden="true" /></button>}
              </div></td>
            </tr>; })}
            {!filtered.length && <tr><td colSpan={3} className="py-12 text-center text-sm text-gray-500">{query ? "No matching members." : "No members yet."}</td></tr>}
          </tbody></table></div>}
      {removing && <RemoveMembers workspace={course} memberRemovePopup={removing} setMemberRemovePopup={setRemoving} setMembers={updateMembers} />}
    </main>
  );
};
export default Learners;
